import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import './BaseTool.css';
import Navbar from '../Page/Navbar';
import Footer from '../Page/Footer';
import api from '../api';
import RatingComponent from './RatingComponent';

const ShortenTool = () => {
    const [inputText, setInputText] = useState('');
    const [outputText, setOutputText] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [targetLength, setTargetLength] = useState('half');
    const [averageRating, setAverageRating] = useState(0);
    const [ratingCount, setRatingCount] = useState(0);

    // Tool ID for rating component
    const toolId = 'shorten';

    useEffect(() => {
        // Fetch ratings for schema.org
        const fetchRatings = async () => {
            try {
                const response = await api.get(`/api/ratings/${toolId}`);
                setAverageRating(response.data.average || 0);
                setRatingCount(response.data.count || 0);
            } catch (err) {
                console.error('Error fetching ratings for schema:', err);
                // Use default values in case of error
                setAverageRating(0);
                setRatingCount(0);
            }
        };

        fetchRatings();
    }, [toolId]);

    const jsonLd = {
        "@context": "https://schema.org",
        "@type": "WebApplication",
        "name": "AI Text Shortener",
        "headline": "AI Text Shortener – Shorten Your Content Instantly Online",
        "description": "Shorten any text with AI. Reduce word count while preserving meaning. Great for emails, blog posts, and summaries.",
        "url": "https://bookwitch.io/tools/shorten",
        "applicationCategory": "WritingApplication",
        "operatingSystem": "Web browser",
        "offers": {
            "@type": "Offer",
            "price": "0",
            "priceCurrency": "USD"
        },
        "featureList": "Context-aware summarization, Fast results, Multilingual support",
        "screenshot": "https://bookwitch.io/images/shorten-tool-screenshot.jpg",
        "creator": {
            "@type": "Organization",
            "name": "Book Witch",
            "url": "https://bookwitch.io"
        },
        "potentialAction": {
            "@type": "UseAction",
            "target": "https://bookwitch.io/tools/shorten"
        }
    };

    const handleProcess = async () => {
        if (!inputText.trim()) return;

        setIsLoading(true);
        try {
            const response = await api.post('/tools/shorten', {
                content: inputText,
                target_length: targetLength
            });
            setOutputText(response.data.modified_content);
        } catch (error) {
            console.error('Shorten error:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const lengthOptions = [
        { value: 'half', label: '50% Shorter' },
        { value: 'third', label: '66% Shorter' },
        { value: 'quarter', label: '75% Shorter' }
    ];

    return (
        <>
            <Helmet>
                <title>AI Text Shortener – Shorten Your Content Instantly Online</title>
                <meta name="description" content="Shorten any text with AI. Reduce word count while preserving meaning. Great for emails, blog posts, and summaries." />


                <meta name="keywords" content="text shortener, content summarizer, AI summarization tool, text compression tool, content shortener, summary generator" />

                <meta property="og:type" content="website" />
                <meta property="og:title" content="AI Text Shortening Tool | Smart Content Summarizer | Book Witch" />
                <meta property="og:description" content="Shorten your content intelligently with our AI-powered tool. Maintain key information while reducing length. Perfect for creating summaries and concise content." />
                <meta property="og:url" content="https://bookwitch.io/tools/shorten" />

                <link rel="canonical" href="https://bookwitch.io/tools/shorten" />
                <script type="application/ld+json">
                    {JSON.stringify(jsonLd)}
                </script>
            </Helmet>

            <Navbar />
            <div className="base-tool">
                <div className="bt-header">
                    <h1>Instantly Shorten Any Text with AI</h1>
                    <p>Need to reduce your content length without losing its meaning? Our <strong>AI text shortener</strong> helps you compress any text while preserving its core message. Whether you're editing emails, blog posts, essays, or social media captions — this tool rewrites your content in a more concise and readable way.</p>
                </div>

                <div className="bt-features">
                    <div className="bt-feature-item">
                        <span className="material-symbols-outlined">psychology</span>
                        <h3>Context-aware</h3>
                        <p>Unlike basic summarizers, this tool understands what matters in your text</p>
                    </div>
                    <div className="bt-feature-item">
                        <span className="material-symbols-outlined">bolt</span>
                        <h3>Fast and efficient</h3>
                        <p>Get instant results with just one click</p>
                    </div>
                    <div className="bt-feature-item">
                        <span className="material-symbols-outlined">language</span>
                        <h3>Multilingual support</h3>
                        <p>Shorten text in English, French, Spanish, and more</p>
                    </div>
                </div>

                <div className="bt-editor">
                    <textarea
                        className="textarea"
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        placeholder="Enter your text here..."
                    />
                    <div className="bt-controls">
                        <select
                            value={targetLength}
                            onChange={(e) => setTargetLength(e.target.value)}
                        >
                            {lengthOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <button
                            onClick={handleProcess}
                            disabled={isLoading || !inputText.trim()}
                            className="button-important"
                        >
                            <div className="inner">
                                {isLoading ? 'Processing...' : 'Shorten'}
                            </div>
                        </button>
                    </div>
                </div>

                {outputText && (
                    <div className="bt-output">
                        <textarea
                            className="textarea"
                            value={outputText}
                            readOnly
                        />
                        <button
                            onClick={() => navigator.clipboard.writeText(outputText)}
                            className="button-hollow"
                        >
                            <span className="material-symbols-outlined">content_copy</span>
                            Copy
                        </button>
                    </div>
                )}

                <RatingComponent toolId={toolId} />

                <section className="bt-section">
                    <div className="bt-section-header">
                        <h2>How it works</h2>
                    </div>

                    <div className="bt-use-cases-grid">
                        <div className="bt-use-case-card">
                            <h3>1. Paste your text</h3>
                            <p>Paste your text into the field above.</p>
                        </div>
                        <div className="bt-use-case-card">
                            <h3>2. Choose length</h3>
                            <p>Choose how much shorter you want it (e.g. 50%, 70%...).</p>
                        </div>
                        <div className="bt-use-case-card">
                            <h3>3. Get results</h3>
                            <p>Click <strong>Shorten</strong> and get a refined version instantly.</p>
                        </div>
                    </div>
                </section>

                <section className="bt-cta">
                    <h2>Use this shortener as a starting point</h2>
                    <p>Want to turn your shortened content into something bigger? You can use the result as a starting point in <strong>Book Witch</strong> — our AI writing assistant that helps you generate full ebooks, guides or lead magnets from any idea.</p>
                    <p>Start small here. Scale big with Book Witch.</p>
                    <div className="bt-cta-buttons">
                        <Link to="/app" className="no-style-link">
                            <button className="button-important">
                                <div className="inner">
                                    Try Book Witch
                                    <span className="material-symbols-outlined">arrow_forward</span>
                                </div>
                            </button>
                        </Link>
                    </div>
                </section>

                <section className="bt-faq">
                    <h2>FAQ – AI Text Shortener</h2>
                    <div className="bt-faq-item">
                        <h3>Can this tool shorten any type of content?</h3>
                        <p>Yes! Whether it's an email, article, essay or caption — our AI adapts to any context.</p>
                    </div>
                    <div className="bt-faq-item">
                        <h3>Will the meaning of my text be preserved?</h3>
                        <p>Absolutely. The AI is designed to keep the core message intact while reducing length.</p>
                    </div>
                </section>
            </div>
            <Footer />
        </>
    );
};

export default ShortenTool; 